import type {
  CatalogRecord,
  CatalogsResponse,
  CompareRecordsResponse,
  CreateRecordInput,
  ErrorResponse,
  RecordsQuery,
  RecordsResponse,
  UpdateRecordInput,
} from '@svetoch/contracts';

export type {
  CatalogRecord, CatalogsResponse, CompareRecordsResponse, CreateRecordInput,
  MaterialType, RecordsQuery, RecordsResponse, RecordStatus, RecordsView,
  RecordSortField, SortDirection, UpdateRecordInput,
} from '@svetoch/contracts';

export class ApiClientError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
  }
}

export interface ApiClientOptions {
  baseUrl?: string;
  /** Allows a platform adapter or a controlled fetch implementation in integration tests. */
  fetch?: typeof globalThis.fetch;
}

export function createApiClient(options: ApiClientOptions = {}) {
  const baseUrl = (options.baseUrl ?? '/api/v1').replace(/\/+$/, '');
  const fetcher = options.fetch ?? globalThis.fetch.bind(globalThis);

  async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    let response: Response;
    try {
      response = await fetcher(`${baseUrl}${path}`, {
        ...init,
        headers: {
          Accept: 'application/json',
          ...(init.body ? { 'Content-Type': 'application/json' } : {}),
          ...init.headers,
        },
      });
    } catch (error) {
      if (init.signal?.aborted || (error instanceof Error && error.name === 'AbortError')) throw error;
      throw new ApiClientError('Не удалось связаться с сервером. Проверьте подключение и повторите попытку.', 0, 'NETWORK_ERROR');
    }

    if (!response.ok) {
      let body: Partial<ErrorResponse> = {};
      try { body = await response.json() as Partial<ErrorResponse>; } catch { /* The proxy may send an HTML error. */ }
      throw new ApiClientError(
        typeof body.message === 'string' ? body.message : `Сервер вернул ошибку ${response.status}.`,
        response.status,
        typeof body.code === 'string' ? body.code : undefined,
      );
    }
    return await response.json() as T;
  }

  return {
    listRecords(query: RecordsQuery = {}, signal?: AbortSignal): Promise<RecordsResponse> {
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(query)) {
        if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
      }
      const search = params.toString();
      return request(`/records${search ? `?${search}` : ''}`, { signal });
    },
    getRecord(id: string, signal?: AbortSignal): Promise<CatalogRecord> {
      return request(`/records/${encodeURIComponent(id)}`, { signal });
    },
    createRecord(input: CreateRecordInput): Promise<CatalogRecord> {
      return request('/records', { method: 'POST', body: JSON.stringify(input) });
    },
    updateRecord(id: string, input: UpdateRecordInput): Promise<CatalogRecord> {
      return request(`/records/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(input) });
    },
    setFavorite(id: string, favorite: boolean): Promise<CatalogRecord> {
      return request(`/records/${encodeURIComponent(id)}/favorite`, { method: 'PUT', body: JSON.stringify({ favorite }) });
    },
    compareRecords(ids: string[]): Promise<CompareRecordsResponse> {
      return request('/records/compare', { method: 'POST', body: JSON.stringify({ ids }) });
    },
    getCatalogs(signal?: AbortSignal): Promise<CatalogsResponse> {
      return request('/catalogs', { signal });
    },
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
